import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { PlatformConnection, PlatformAnalytics } from '../lib/types';

export const usePlatformAnalytics = (connectionId: string) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<PlatformAnalytics[]>([]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_analytics')
          .select('*')
          .eq('connection_id', connectionId)
          .order('date', { ascending: false })
          .limit(30);

        if (error) throw error;

        setAnalytics(data.map(item => ({
          date: item.date,
          metrics: item.metrics,
        })));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
      } finally {
        setLoading(false);
      }
    };

    if (connectionId) {
      fetchAnalytics();
    }
  }, [connectionId]);

  return { analytics, loading, error };
};

export const useConnectedPlatforms = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data, error } = await supabase
          .from('platform_connections')
          .select('*')
          .eq('is_active', true);

        if (error) throw error;

        setConnections(data.map(item => ({
          id: item.id,
          platformId: item.platform_id,
          platformType: item.platform_type,
          connectedAt: item.connected_at,
          lastSync: item.last_sync,
          isActive: item.is_active,
        })));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch connections');
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
  }, []);

  return { connections, loading, error };
};